import {
  FEATURE_LABELS,
  SensitivityFeatureKey,
  defaultSensitivityFeatureKeys,
  getSensitivityModelWarningsForFeature,
} from '@aloft/toolkit-shared';
import { Flex, Icon, SimpleSelect, Typography, colors } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-direct-chakra, aloft/prefer-sound-ui-button, import/no-extraneous-dependencies
import {
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';

import { useMemo, useState } from 'react';

import { PropertyGrid } from '../../../components/PropertyGrid/PropertyGrid.tsx';
import { PropertyGridSensitivityContext } from '../../../components/PropertyGrid/PropertyGridSensitivityContext.tsx';
import { SensitivityRateSection } from '../../../components/PropertyGrid/columnSections/SensitivityRateSection.tsx';
import { SensitivityUnitRateLookupSection } from '../../../components/PropertyGrid/columnSections/SensitivityUnitRateLookupSection.tsx';
import { selectSensitivityAnalysis } from '../../../redux/features/selectors/unitRatesSelectors/selectUnitRates.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { SensitivityModalGraphSection } from './SensitivityModalGraphSection.tsx';
import { StatsRow } from './StatsRow.tsx';

import styles from './SensitivityAnalysisModal.module.css';

export const SensitivityAnalysisModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [selectedFeatureKey, setSelectedFeatureKey] = useState<SensitivityFeatureKey>('gla');

  const sensitivityAnalysis = useAppSelector(selectSensitivityAnalysis);

  const selectedResult = sensitivityAnalysis?.[selectedFeatureKey];

  const sensitivityContextValue = useMemo(
    () => ({ sensitivityResult: selectedResult, sensitivityFeatureKey: selectedFeatureKey }),
    [selectedResult, selectedFeatureKey],
  );

  const sensitivityModelWarnings = getSensitivityModelWarningsForFeature(
    sensitivityAnalysis,
    selectedFeatureKey,
  );

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent bg={colors.surface3} onKeyDown={(e) => e.stopPropagation()}>
        <ModalCloseButton />
        <ModalBody padding="1.5rem">
          <Flex flexDirection="column" gap="1rem">
            <Flex alignItems="center" gap="0.75rem">
              <Icon icon="speedometer" size="xl" />
              <Typography variant="h1">Sensitivity Analysis Model</Typography>
            </Flex>

            <Flex flexDirection="column" gap="0.25rem">
              <Typography
                variant="body2"
                color="textSecondary"
                __dangerDoNotUseSx={{ maxWidth: 'none' }}
              >
                Toolkit&apos;s sensitivity model is built using an assumption on the most likely
                values for a given variable. Select a variable to see the analysis details:
              </Typography>
              {sensitivityModelWarnings
                .filter(
                  (warning) =>
                    warning.warning !== null && ['UNRELIABLE', 'WEAK'].includes(warning.label),
                )
                .map((warning) => (
                  <Flex key={warning.label} alignItems="center" gap="0.5rem" paddingRight="1rem">
                    <Icon
                      icon="error-warning"
                      size="md"
                      color={warning.label === 'UNRELIABLE' ? 'textError' : 'textWarning'}
                    />
                    <Typography
                      variant="tableH1"
                      __dangerDoNotUseSx={{ maxWidth: 'none' }}
                      color="textPrimary"
                    >
                      {warning.warning!}
                    </Typography>
                  </Flex>
                ))}
            </Flex>

            {sensitivityAnalysis ? (
              <>
                <Flex alignItems="center" gap="0.5rem" width="fit-content">
                  <Typography variant="body2" color="textSecondary">
                    Variable:
                  </Typography>
                  <SimpleSelect
                    size="sm"
                    value={selectedFeatureKey}
                    onChange={(e) => setSelectedFeatureKey(e.target.value as SensitivityFeatureKey)}
                  >
                    {defaultSensitivityFeatureKeys.map((key) => (
                      <option key={key} value={key}>
                        {FEATURE_LABELS[key]}
                      </option>
                    ))}
                  </SimpleSelect>
                </Flex>

                {selectedResult && (
                  <>
                    <Flex justifyContent="center">
                      <StatsRow
                        type="sensitivity"
                        result={selectedResult}
                        featureKey={selectedFeatureKey}
                      />
                    </Flex>
                    {selectedResult.insufficientVariation ? (
                      <Typography variant="body2" color="textSecondary">
                        There is not enough variation in {FEATURE_LABELS[selectedFeatureKey]} across
                        your comparables to run a meaningful sensitivity analysis.
                      </Typography>
                    ) : (
                      <>
                        <SensitivityModalGraphSection
                          result={selectedResult}
                          featureKey={selectedFeatureKey}
                        />
                        <Flex className={styles.propertyGridScroll} width="100%" maxHeight="50vh">
                          <PropertyGridSensitivityContext.Provider value={sensitivityContextValue}>
                            <PropertyGrid
                              gridType="SensitivityGrid"
                              columnSections={[
                                SensitivityUnitRateLookupSection,
                                SensitivityRateSection,
                              ]}
                              labelColumnHeader={
                                <Flex
                                  width="100%"
                                  alignItems="center"
                                  gap="0.5rem"
                                  padding="0.5rem"
                                  backgroundColor="surface4"
                                >
                                  <Typography variant="label" color="textSecondary">
                                    Property
                                  </Typography>
                                </Flex>
                              }
                            />
                          </PropertyGridSensitivityContext.Provider>
                        </Flex>
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Sensitivity analysis data is not available.
              </Typography>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </ChakraModal>
  );
};
